import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Briefcase, Hash, Download } from "lucide-react";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";
import { useToast } from "@/hooks/use-toast";
import type { ParsedJob, MetricQuestion } from "@/pages/ResumeQuantification";

interface QuantificationQuestionsStepProps {
  parsedJobs: ParsedJob[];
  questions: Record<string, MetricQuestion[]>;
  firstName: string;
  lastName: string;
  onComplete: (answers: Record<string, Record<string, string>>) => void;
  onBack: () => void;
}

export function QuantificationQuestionsStep({
  parsedJobs,
  questions,
  firstName,
  lastName,
  onComplete,
  onBack,
}: QuantificationQuestionsStepProps) {
  const { toast } = useToast();
  // Initialize answers state: jobId -> questionId -> answer
  const [answers, setAnswers] = useState<Record<string, Record<string, string>>>(() => {
    const initial: Record<string, Record<string, string>> = {};
    parsedJobs.forEach(job => {
      initial[job.id] = {};
    });
    return initial;
  });

  const handleAnswerChange = (jobId: string, questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [jobId]: {
        ...prev[jobId],
        [questionId]: value,
      },
    }));
  };

  const handleFinish = () => {
    onComplete(answers);
  };

  const handleDownloadQuestions = async () => {
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      const documentTitle = fullName ? `${fullName} - Metrics Questions for Resume` : "Metrics Questions for Resume";
      
      const sections: Paragraph[] = [
        new Paragraph({
          spacing: { after: 400 },
          children: [
            new TextRun({ text: documentTitle, bold: true, size: 32, color: "000000" }),
          ],
        }),
        new Paragraph({
          spacing: { after: 400 },
          children: [
            new TextRun({ text: "Answer these questions to add impactful numbers to your resume bullets.", size: 24, color: "000000" }),
          ],
        }),
      ];

      parsedJobs.forEach((job) => {
        const jobQuestions = questions[job.id] || [];
        
        // Job header - 14pt (28 half-points)
        sections.push(
          new Paragraph({
            spacing: { before: 400, after: 200 },
            children: [
              new TextRun({ text: `${job.title} at ${job.company}`, bold: true, size: 28, color: "000000" }),
            ],
          }),
          new Paragraph({
            spacing: { after: 300 },
            children: [
              new TextRun({ text: job.dates, size: 24, color: "000000" }),
            ],
          })
        );

        // Questions - 12pt (24 half-points)
        jobQuestions.forEach((q, index) => {
          sections.push(
            new Paragraph({
              spacing: { before: 200, after: 100 },
              children: [
                new TextRun({ text: `${index + 1}. ${q.question}`, size: 24, color: "000000" }),
              ],
            }),
            new Paragraph({
              spacing: { after: 300 },
              children: [],
            })
          );
        });
      });

      const doc = new Document({
        sections: [{
          properties: {},
          children: sections,
        }],
      });

      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = fullName.replace(/[^a-zA-Z0-9]/g, '_') || 'resume';
      a.download = `${safeName}_metrics_questions.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({ title: "Questions downloaded!", description: "Open in Word to fill in your answers." });
    } catch (error) {
      console.error("Download error:", error);
      toast({ title: "Download failed", variant: "destructive" });
    }
  };

  const totalQuestions = Object.values(questions).flat().length;
  const answeredQuestions = Object.values(answers)
    .flatMap(jobAnswers => Object.values(jobAnswers))
    .filter(a => a && a.trim() !== "").length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-2">Answer Quick Questions</h2>
        <p className="text-muted-foreground mb-4">
          Enter numbers that will make your resume stand out. Skip any you don't know.
        </p>
        <div className="flex items-center justify-center gap-4">
          <Badge variant="secondary" className="text-sm">
            {answeredQuestions} of {totalQuestions} answered
          </Badge>
          <Button variant="outline" size="sm" onClick={handleDownloadQuestions}>
            <Download className="h-4 w-4 mr-2" />
            Download Questions (Word)
          </Button>
        </div>
      </div>

      {/* Jobs and Questions */}
      <div className="space-y-8">
        {parsedJobs.map((job) => {
          const jobQuestions = questions[job.id] || [];
          
          return (
            <Card key={job.id} className="p-6">
              {/* Job Header */}
              <div className="flex items-start gap-3 mb-6 pb-4 border-b">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Briefcase className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{job.title}</h3>
                  <p className="text-sm text-muted-foreground">{job.company} • {job.dates}</p>
                </div>
              </div>

              {/* Questions Grid */}
              <div className="space-y-4">
                {jobQuestions.length > 0 ? (
                  jobQuestions.map((q) => {
                    const isAnswered = answers[job.id]?.[q.id]?.trim() !== "" && answers[job.id]?.[q.id] !== undefined;
                    return (
                      <div
                        key={q.id}
                        className={`flex items-center gap-4 p-4 rounded-lg transition-colors border-2 ${
                          isAnswered 
                            ? "border-green-500 bg-green-50 dark:bg-green-950/20" 
                            : "border-red-400 bg-red-50/50 dark:bg-red-950/20"
                        }`}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground mb-1">{q.question}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            Related to: "{q.bulletText}"
                          </p>
                        </div>
                        <div className="flex items-center gap-2 w-32">
                          <Hash className={`h-4 w-4 ${isAnswered ? "text-green-600" : "text-muted-foreground"}`} />
                          <Input
                            type="text"
                            placeholder={q.placeholder}
                            value={answers[job.id]?.[q.id] || ""}
                            onChange={(e) => handleAnswerChange(job.id, q.id, e.target.value)}
                            className={`h-9 text-right font-medium ${isAnswered ? "border-green-500" : ""}`}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No questions generated for this position
                  </p>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-6 border-t">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Button onClick={handleFinish} size="lg">
          Finish & Review
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
